import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { link, routes } from '@aline/routes';
import { useReactiveVar } from '@apollo/client/react';
import clsx from 'clsx';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { ChatPanel } from 'src/components/ai/chat';
import type { SelectedPlaybookRun } from 'src/components/ai/chat/chat-hooks/useSelectedPlaybookRun';
import { chatDrillInResetVar } from 'src/components/ai/chat/chat-hooks/useSelectedPlaybookRun';
import { $chatThreadState } from 'src/components/ai/chat/chat-state';
import type { AIChatItem } from 'src/components/ai/chat/ChatPanelMarkdown/helpers/aiChatItem';
import {
  useAIChatItemParam,
  useScrollToChatItemEffect,
} from 'src/components/ai/chat/ChatPanelMarkdown/hooks/useScrollToChatItem';
import { PanelHeader, SmallCloseIconButton, Spacer } from 'src/components/generic';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { useVisiblePageContext } from './use-visible-page-context';

const useStyles = makeStyles()(theme => ({
  chatPanel: {
    marginBottom: theme.spacing(2),
  },
  wrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  wrapperPage: {
    backgroundColor: theme.palette.background.default,
  },
}));

/**
 * Singleton host for the AI chat in the global right drawer. Page, drawer,
 * and workflow-thread modes must render one tree with ChatPanel in a stable
 * child position — if modes returned different JSX shapes, React would
 * remount ChatPanel on every switch, losing scroll position and panel-local
 * state.
 */
export function AIChatDrawerContent(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { closeDrawer } = useGlobalDrawer();
  const visibleContext = useVisiblePageContext();
  const { isPageMode, setVisibleThread, threadId, workflowThreadContext } = useVisibleChatThread();
  const [selectedPlaybookRun, setSelectedPlaybookRun] = useThreadScopedPlaybookRun(threadId);
  const handleCreateNewChat = useCallback(() => setVisibleThread(null), [setVisibleThread]);
  const handleBackToWorkflowActivity = useCallback(() => {
    if (!workflowThreadContext) return;

    navigate(
      workflowThreadContext.taskId
        ? link.WorkflowDetailTabTask({
            taskId: workflowThreadContext.taskId,
            workflowId: workflowThreadContext.workflowId,
          })
        : link.WorkflowDetailTabComments({ workflowId: workflowThreadContext.workflowId })
    );
  }, [navigate, workflowThreadContext]);
  const { backButtonTooltip, headerText } = getHeaderConfig(workflowThreadContext);
  const isWorkflowThreadMode = Boolean(workflowThreadContext);

  useChatItemDeepLinks(threadId, setVisibleThread);

  return (
    <div className={clsx(classes.wrapper, isPageMode && classes.wrapperPage)}>
      {!isPageMode && !selectedPlaybookRun && (
        <PanelHeader
          backButtonTooltip={backButtonTooltip}
          showBackButton={isWorkflowThreadMode}
          text={headerText}
          onClickBack={handleBackToWorkflowActivity}
        >
          <Spacer />
          <SmallCloseIconButton onClick={closeDrawer} />
        </PanelHeader>
      )}
      <ChatPanel
        registerGlobalCallbacks
        className={isPageMode ? undefined : classes.chatPanel}
        hideFeatureCards={!isPageMode}
        mode={isPageMode ? 'page' : 'drawer'}
        selectedPlaybookRun={selectedPlaybookRun}
        threadId={threadId}
        visibleContext={visibleContext}
        onClose={!isPageMode ? closeDrawer : undefined}
        onCreateNewChat={isWorkflowThreadMode ? undefined : handleCreateNewChat}
        onNavigateToThread={setVisibleThread}
        onSelectedPlaybookRunChange={setSelectedPlaybookRun}
        onThreadHeaderGoBack={isWorkflowThreadMode ? handleBackToWorkflowActivity : undefined}
      />
    </div>
  );
}

interface WorkflowThreadRouteContext {
  threadId: UUID;
  workflowId: UUID;
  taskId?: UUID;
}

type SetVisibleThread = (threadId: UUID | null, options?: { replace?: boolean }) => void;

/**
 * Which thread the singleton chat shows, in which presentation mode, and how
 * to change it.
 *
 * Read precedence: a workflow/task thread route wins, then the maximized
 * /chat page route, then the docked drawer's aiThreadIdVar. The URL is the
 * source of truth whenever a thread route is active — nothing is copied into
 * local state, so back/forward and deep links resolve on their own.
 * aiThreadIdVar exists only because the docked drawer has no route; the
 * effect below is the single URL -> var write, a handoff so the drawer keeps
 * showing the routed thread after it docks.
 *
 * setVisibleThread is the write-side mirror: it updates whichever location is
 * canonical for the current mode (workflow thread route, /chat route, or the
 * var), so callers never branch on mode themselves.
 */
function useVisibleChatThread() {
  const navigate = useNavigate();
  const { variant } = useGlobalDrawer();
  const drawerThreadId = useReactiveVar(aiThreadIdVar);
  const { threadId: routeThreadId } = useParams<{ threadId?: string }>();
  const taskThreadMatch = useMatch(routes.WorkflowDetailTabTaskThread);
  const workflowThreadMatch = useMatch(routes.WorkflowDetailTabThread);

  // useMatch memoizes per location, so the match objects are stable deps.
  const workflowThreadContext = useMemo<WorkflowThreadRouteContext | null>(() => {
    if (taskThreadMatch) {
      const { taskId, threadId, workflowId } = taskThreadMatch.params;
      if (taskId && threadId && workflowId) return { taskId, threadId, workflowId };
    }
    if (workflowThreadMatch) {
      const { threadId, workflowId } = workflowThreadMatch.params;
      if (threadId && workflowId) return { threadId, workflowId };
    }
    return null;
  }, [taskThreadMatch, workflowThreadMatch]);
  const isPageMode = variant === 'maximized';
  const threadId =
    workflowThreadContext?.threadId ?? (isPageMode ? (routeThreadId ?? null) : drawerThreadId);

  const setVisibleThread = useCallback<SetVisibleThread>(
    (newThreadId, options) => {
      if (workflowThreadContext && newThreadId) {
        navigate(getWorkflowThreadRoute(workflowThreadContext, newThreadId), options);
        return;
      }

      if (isPageMode) {
        navigate(
          newThreadId ? link.ChatDetail({ threadId: newThreadId }) : link.ChatIndex(),
          options
        );
        return;
      }

      aiThreadIdVar(newThreadId);
    },
    [isPageMode, navigate, workflowThreadContext]
  );

  useEffect(() => {
    const routedThreadId = workflowThreadContext?.threadId;
    if (routedThreadId) {
      aiThreadIdVar(routedThreadId);
      return;
    }

    if (isPageMode) aiThreadIdVar(routeThreadId ?? null);
  }, [isPageMode, routeThreadId, workflowThreadContext]);

  return {
    isPageMode,
    setVisibleThread,
    threadId,
    workflowThreadContext,
  };
}

/**
 * Playbook run drill-in state, scoped to the visible thread. A selection is
 * transient — cleared when the visible thread changes (so revisiting a thread
 * lands on the conversation view) and when a "view thread fresh" navigation
 * bumps chatDrillInResetVar (activity agent cards can target a thread already
 * open here with a run drilled in). Synced during render so the run panel
 * never flashes for the new scope.
 */
function useThreadScopedPlaybookRun(threadId: UUID | null) {
  const drillInResetCount = useReactiveVar(chatDrillInResetVar);
  const [lastScope, setLastScope] = useState({
    drillInResetCount,
    threadId,
  });
  const [selectedPlaybookRun, setSelectedPlaybookRun] = useState<SelectedPlaybookRun | null>(null);
  if (lastScope.threadId !== threadId || lastScope.drillInResetCount !== drillInResetCount) {
    setLastScope({ drillInResetCount, threadId });
    setSelectedPlaybookRun(null);
  }

  return [selectedPlaybookRun, setSelectedPlaybookRun] as const;
}

/**
 * Deep-link support: an ?aiChatItem= URL param scrolls the chat to a specific
 * message, switching the visible thread first when the item belongs to a
 * different one.
 */
function useChatItemDeepLinks(threadId: UUID | null, setVisibleThread: SetVisibleThread) {
  const setAutoScrollAIChatItem = useCallback(
    (aiChatItem: AIChatItem) => {
      aiThreadIdVar(aiChatItem.threadId);
      if (aiChatItem.threadId !== threadId)
        setVisibleThread(aiChatItem.threadId, { replace: true });

      $chatThreadState.autoScrollAIChatItem = aiChatItem;
    },
    [setVisibleThread, threadId]
  );

  useScrollToChatItemEffect();
  useAIChatItemParam(setAutoScrollAIChatItem);
}

function getWorkflowThreadRoute(context: WorkflowThreadRouteContext, threadId: UUID) {
  return context.taskId
    ? link.WorkflowDetailTabTaskThread({
        taskId: context.taskId,
        threadId,
        workflowId: context.workflowId,
      })
    : link.WorkflowDetailTabThread({ threadId, workflowId: context.workflowId });
}

/**
 * Header copy for each drawer mode: workflow task agent, workflow-level AI
 * thread, or the default AI chat (no back button).
 */
function getHeaderConfig(context: WorkflowThreadRouteContext | null) {
  if (!context) return { backButtonTooltip: undefined, headerText: 'AI Chat' };
  if (context.taskId) return { backButtonTooltip: 'Back to task', headerText: 'Task Agent' };
  return { backButtonTooltip: 'Back to workflow activity', headerText: 'AI Thread' };
}
